import { getUserDetails, makeGetCall, makePostCall } from './helper';

/**
 * Dark theme for the PACS List.
 *
 * Radiologists read in dimmed rooms and the study list is the one bright surface
 * they stare at between studies (the OHIF viewer is already dark). This module is
 * the on/off switch behind the header toggle; `styles/pacs-dark.css` holds the
 * actual palette.
 *
 * SCOPE — deliberately narrow for now. Dark is applied only while a themed tab
 * (PACS List, My Worklist, Orders) is the active tab; switching to Dispatch /
 * Front Desk / … drops straight back to light even though the preference stays
 * 'dark'. See feature-docs/DARK_THEME_HANDOFF.md for how to extend it to another
 * page (short version: theme that page's CSS, then add its key to
 * `THEMED_TAB_KEYS` in customApp).
 *
 * The flag lives on <html> as `data-pacs-theme="dark"`, NOT on a page div, because
 * antd renders Select dropdowns, DatePicker panels, Tooltips and Modals into
 * portals hung off <body> — a class on a nested element never reaches them.
 */
export type PacsTheme = 'light' | 'dark';

export const DEFAULT_PACS_THEME: PacsTheme = 'light';

/**
 * Persisted in the generic `user_preferences` table (up_username, up_type, up_value
 * JSONB) that already backs mouse actions, favourite tools, hotkeys, panel positions
 * and the workstation layout — so this needs no migration and no new column, just a
 * new up_type. Stored as an object rather than a bare string so further appearance
 * settings (density, accent colour) can join it later without another preference type.
 */
const PREFERENCE_TYPE = 'pacs_theme';

/**
 * localStorage is ONLY a synchronous cache of the row above, never the source of
 * truth. It exists so the first paint after a reload is already the right theme
 * instead of flashing white while the preference round-trips. Logout calls
 * localStorage.clear(); preloadPacsTheme() refills it from the database on the next
 * boot, which is a full page load after every login.
 *
 * Keyed by username so a shared reading-room workstation can never serve one
 * radiologist's theme to the next person who logs in.
 */
const STORAGE_KEY = 'pacs_theme';

/** Attribute + value that every rule in styles/pacs-dark.css is scoped under. */
const ROOT_ATTRIBUTE = 'data-pacs-theme';

type ThemeSubscriber = (theme: PacsTheme) => void;

const subscribers = new Set<ThemeSubscriber>();

const currentUsername = (): string => {
  try {
    const user = getUserDetails();
    return user?.username || user?.user_name || '';
  } catch {
    return '';
  }
};

const asTheme = (value: any): PacsTheme | null =>
  value === 'dark' || value === 'light' ? value : null;

const readCache = (): Record<string, PacsTheme> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Corrupt JSON, or storage blocked on a locked-down browser profile.
    return {};
  }
};

const writeCache = (username: string, theme: PacsTheme): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readCache(), [username]: theme })
    );
  } catch (e) {
    console.warn('pacsTheme: failed to write localStorage cache', e);
  }
};

/** Synchronous read of the user's stored preference. */
export const getPacsTheme = (): PacsTheme => {
  const username = currentUsername();
  if (!username) return DEFAULT_PACS_THEME;

  return asTheme(readCache()[username]) || DEFAULT_PACS_THEME;
};

export const isPacsDark = (): boolean => getPacsTheme() === 'dark';

/**
 * Add or remove the <html> attribute the dark stylesheet keys off.
 *
 * `active` is NOT the same thing as the stored preference: it is the preference
 * AND the PACS List being on screen. customApp owns that AND; everything else just
 * reads the attribute.
 */
export const applyPacsTheme = (active: boolean): void => {
  try {
    const root = document.documentElement;
    if (active) {
      root.setAttribute(ROOT_ATTRIBUTE, 'dark');
    } else {
      root.removeAttribute(ROOT_ATTRIBUTE);
    }
  } catch (e) {
    console.warn('pacsTheme: failed to apply root attribute', e);
  }
};

/** True when dark is currently painted (not merely preferred). */
export const isPacsDarkActive = (): boolean => {
  try {
    return document.documentElement.getAttribute(ROOT_ATTRIBUTE) === 'dark';
  } catch {
    return false;
  }
};

export const subscribePacsTheme = (
  callback: ThemeSubscriber,
  emitCurrent = true
): (() => void) => {
  subscribers.add(callback);

  if (emitCurrent) {
    callback(getPacsTheme());
  }

  return () => {
    subscribers.delete(callback);
  };
};

/**
 * Persist the preference and tell every listener. The cache is written first so a
 * reload mid-flight still comes back on the new theme, and the subscribers fire
 * before the request resolves so the flip feels instant.
 *
 * Resolves false when the row could not be saved, so the caller can tell the user it
 * will not follow them to another machine.
 */
export const setPacsTheme = async (theme: PacsTheme): Promise<boolean> => {
  const username = currentUsername();

  if (username) writeCache(username, theme);

  subscribers.forEach(callback => {
    try {
      callback(theme);
    } catch (error) {
      console.error('[pacsTheme] subscriber error', error);
    }
  });

  if (!username) return false;

  try {
    await makePostCall('/save-user-preference', {
      username,
      type: PREFERENCE_TYPE,
      value: { theme },
    });
    return true;
  } catch (e) {
    console.error('pacsTheme: failed to persist preference', e);
    return false;
  }
};

/**
 * Hydrate the cache from the database. Called from index.js before the app mounts so
 * the PACS List paints dark on the first frame rather than flashing white. Safe no-op
 * when nobody is logged in.
 */
export const preloadPacsTheme = async (): Promise<PacsTheme | null> => {
  const username = currentUsername();
  if (!username) return null;

  try {
    const resp = await makeGetCall(
      `/get-user-preference?username=${encodeURIComponent(username)}&type=${PREFERENCE_TYPE}`
    );
    const stored = resp?.data?.data;
    const theme = asTheme(typeof stored === 'string' ? stored : stored?.theme);

    // A successful fetch is authoritative: mirror exactly what the row holds (the
    // default when there is no row yet) so a preference cleared server-side cannot
    // linger here.
    writeCache(username, theme || DEFAULT_PACS_THEME);
    return theme;
  } catch (e) {
    // Offline or the endpoint failed — keep whatever the cache already holds.
    console.warn('pacsTheme: preload failed, using cached/default theme', e);
    return null;
  }
};
